import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectWithoutProperties from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["scrollToChallengeTop"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference } from '@apollo/client';
import { ActivitySelector, DESKTOP_DRAWER_WIDTH, MOBILE_DRAWER_WIDTH } from '@infl-ts/ui-advocate-components';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useToggle } from '@infl-ts/util-hooks';
import { logError } from '@infl-ts/util-query-errors';
import { join } from '@infl-ts/util-string-join';
import { ChallengePlayerContext, PublicResponseStageNames } from '@infl-ts/util-use-challenge-player';
import SlideIcon from '@mui/icons-material/KeyboardTab';
import { Box, Button, Typography } from '@mui/material';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { constant, constNull, constVoid, flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Translation, useTranslation } from 'react-i18next';
import { getLastTimelineEntry } from '../challenge-player/stages/corporate-approval';
import { completeStageFailure, completeStageFailureWithMessage, limitReachedFailure, unknownFailure } from './alerts';
import ChallengeHeader from './challenge-header';
import { NonRejectedCorporateAlertEntries } from './codecs';
import CompletionScreen from './completion-screen';
import CorporateConfirmationAlert from './corporate-confirmation-alert';
import FirstStageInfo from './first-stage-info';
import { useCompleteStage, useUpdateChallengeAsOpened } from './mutations';
import PublicResponses from './public-response';
import CurrentStage from './stages/index';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var useMarkAsSeen = function useMarkAsSeen(challenge) {
  var _useUpdateChallengeAs = useUpdateChallengeAsOpened({
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref) {
        var id = _ref.id,
            unseen = _ref.unseen;
        return cache.modify({
          id: cache.identify(makeReference("Challenge:".concat(id))),
          fields: {
            unseen: constant(unseen)
          }
        });
      }));
    }
  }),
      _useUpdateChallengeAs2 = _slicedToArray(_useUpdateChallengeAs, 1),
      updateChallengeAsOpened = _useUpdateChallengeAs2[0];

  useEffect(function () {
    if (challenge.unseen) void updateChallengeAsOpened({
      variables: {
        id: challenge.id
      }
    });
  }, [challenge.id, challenge.unseen, updateChallengeAsOpened]);
};

var useProps = function useProps(_ref2) {
  var _challengePlayerState, _challengePlayerState2;

  var scrollToChallengeTop = _ref2.scrollToChallengeTop,
      props = _objectWithoutProperties(_ref2, _excluded);

  var playerBodyRef = useRef(null);

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useContext2 = useContext(ChallengePlayerContext),
      closeChallengePlayer = _useContext2.closeChallengePlayer,
      challengePlayerState = _useContext2.challengePlayerState,
      advanceStage = _useContext2.advanceStage;

  var _useToggle = useToggle(false),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      showCompleteAnimation = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      enableCompleteAnimation = _useToggle2$.on,
      disableCompleteAnimation = _useToggle2$.off;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  useEffect(function () {
    scrollToChallengeTop();
  }, [(_challengePlayerState = challengePlayerState.activity) === null || _challengePlayerState === void 0 ? void 0 : (_challengePlayerState2 = _challengePlayerState.currentStage) === null || _challengePlayerState2 === void 0 ? void 0 : _challengePlayerState2.id]);
  var handleChallengeFailure = useCallback(function (alertConfig, errors) {
    showAlert(alertConfig);
    logError("Unable to complete challenge stage: ".concat(errors));
  }, [showAlert]);

  var _useCompleteStage = useCompleteStage({
    onCompleted: E.match(function (e) {
      return handleChallengeFailure(unknownFailure(t), e.toString());
    }, function (_ref3) {
      var completeStage = _ref3.completeStage;

      if (completeStage.__typename === 'InProgressChallengeResult' || completeStage.__typename === 'CompleteChallengeResult') {
        advanceStage(completeStage.activity);
        enableCompleteAnimation();
      } else {
        var _completeStage$errors;

        var errorMessage = pipe(completeStage.errors, A.map(function (e) {
          return e.message;
        }), join(', ')); // Remove "activity is unprocessable: " prefix if present

        var cleanErrorMessage = errorMessage.startsWith('activity is unprocessable: ') ? errorMessage.replace('activity is unprocessable: ', '') : errorMessage;
        handleChallengeFailure(((_completeStage$errors = completeStage.errors[0]) === null || _completeStage$errors === void 0 ? void 0 : _completeStage$errors.__typename) === 'LimitReachedError' ? limitReachedFailure(t) : cleanErrorMessage ? completeStageFailureWithMessage(cleanErrorMessage) : completeStageFailure(t), errorMessage);
      }
    }),
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref4) {
        var completeStage = _ref4.completeStage;

        if (completeStage.__typename === 'InProgressChallengeResult' || completeStage.__typename === 'CompleteChallengeResult') {
          cache.evict({
            id: cache.identify(props.profile),
            fieldName: 'currentPoints'
          });
          cache.evict({
            id: cache.identify(props.profile),
            fieldName: 'lifetimePoints'
          }); // TODO: Remove in ADVO2-2287

          cache.modify({
            id: cache.identify(makeReference("Member:member_".concat(props.profile.id))),
            fields: {
              lifetimePoints: function lifetimePoints(points) {
                return points + completeStage.accruedPoints;
              }
            }
          });
          cache.modify({
            id: cache.identify(makeReference("Member:".concat(props.profile.id))),
            fields: {
              lifetimePoints: function lifetimePoints(points) {
                return points + completeStage.accruedPoints;
              }
            }
          });

          if (props.challenge.multiComplete) {
            cache.modify({
              id: cache.identify(makeReference("Challenge:".concat(props.challenge.id))),
              fields: {
                activities: function activities(currentActivities) {
                  var activities = currentActivities || [];
                  pipe(activities, A.findIndex(function (activity) {
                    return activity.id === completeStage.activity.id;
                  }), O.map(function (i) {
                    return A.updateAt(i, completeStage.activity)(activities);
                  }), O.getOrElseW(function () {
                    return [completeStage.activity].concat(_toConsumableArray(activities));
                  }));
                }
              }
            });
          }
        }
      }));
    }
  }),
      _useCompleteStage2 = _slicedToArray(_useCompleteStage, 2),
      completeStage = _useCompleteStage2[0],
      completeStageStatus = _useCompleteStage2[1];

  useMarkAsSeen(props.challenge);
  return _objectSpread(_objectSpread({}, props), {}, {
    playerBodyRef: playerBodyRef,
    closeChallengePlayer: closeChallengePlayer,
    challengePlayerState: challengePlayerState,
    showCompleteAnimation: showCompleteAnimation,
    disableCompleteAnimation: disableCompleteAnimation,
    completeStage: completeStage,
    completeStageStatus: completeStageStatus
  });
};

var FlexibleChallengeContainer = function FlexibleChallengeContainer(_ref5) {
  var playerBodyRef = _ref5.playerBodyRef,
      isOpen = _ref5.isOpen,
      children = _ref5.children,
      offsetHeight = _ref5.offsetHeight;
  return /*#__PURE__*/_jsx(Box, {
    ref: playerBodyRef,
    sx: [{
      transition: function transition(theme) {
        return theme.transitions.create(['width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen
        });
      },
      width: '100%',
      flexGrow: {
        xs: 'inherit',
        md: 1
      }
    }, isOpen && {
      display: {
        xs: 'none',
        md: 'flex'
      },
      width: {
        xs: 0,
        md: "calc(100% - ".concat(MOBILE_DRAWER_WIDTH, "px)"),
        lg: "calc(100% - ".concat(DESKTOP_DRAWER_WIDTH, "px)")
      },
      overflow: 'scroll',
      transition: function transition(theme) {
        return theme.transitions.create(['width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        });
      }
    }, {
      display: 'flex',
      flexDirection: 'column',
      height: 'max-content',
      minHeight: "calc(100vh - ".concat(offsetHeight, "px)"),
      overflow: 'hidden'
    }],
    "data-element": "Box",
    "data-component": "FlexibleChallengeContainer",
    "data-source-file": "player-body.tsx",
    children: children
  });
};

var SubmissionsButton = function SubmissionsButton(_ref6) {
  var onClick = _ref6.onClick;
  return /*#__PURE__*/_jsx(Button, {
    sx: {
      ml: 'auto',
      zIndex: function zIndex(t) {
        return t.zIndex._3;
      }
    },
    variant: "text",
    onClick: onClick,
    endIcon: /*#__PURE__*/_jsx(SlideIcon, {
      sx: {
        transform: 'rotate(180deg)'
      }
    }),
    "data-element": "Button",
    "data-component": "SubmissionsButton",
    "data-source-file": "player-body.tsx",
    children: /*#__PURE__*/_jsx(Translation, {
      "data-element": "Translation",
      "data-component": "SubmissionsButton",
      "data-source-file": "player-body.tsx",
      children: function children(t) {
        return t('View your submissions');
      }
    })
  });
};

var StageInfo = function StageInfo(_ref7) {
  var stage = _ref7.stage,
      totalStages = _ref7.totalStages;
  return /*#__PURE__*/_jsxs(Typography, {
    sx: {
      py: 1.5,
      mr: 'auto',
      color: function color(t) {
        return t.palette.text.secondary;
      }
    },
    variant: "body2",
    "data-element": "Typography",
    "data-component": "StageInfo",
    "data-source-file": "player-body.tsx",
    children: [stage.position + 1, "/", totalStages, /*#__PURE__*/_jsx(Translation, {
      "data-element": "Translation",
      "data-component": "StageInfo",
      "data-source-file": "player-body.tsx",
      children: function children(t) {
        return /*#__PURE__*/_jsx(Box, {
          component: "span",
          sx: {
            ml: 2
          },
          children: t(stage.name)
        });
      }
    })]
  });
};

export var PurePlayerBody = function PurePlayerBody(_ref8) {
  var _challengeHeaderRef$c, _challengeHeaderRef$c2, _challengePlayerState4, _challengePlayerState5, _challengePlayerState6, _challengePlayerState7, _challenge$activities, _challenge$activities2, _challengePlayerState8;

  var challenge = _ref8.challenge,
      profile = _ref8.profile,
      isOnboarding = _ref8.isOnboarding,
      canClose = _ref8.canClose,
      challengePlayerState = _ref8.challengePlayerState,
      closeChallengePlayer = _ref8.closeChallengePlayer,
      showCompleteAnimation = _ref8.showCompleteAnimation,
      disableCompleteAnimation = _ref8.disableCompleteAnimation,
      completeStage = _ref8.completeStage,
      completeStageStatus = _ref8.completeStageStatus,
      playerBodyRef = _ref8.playerBodyRef;

  var _useToggle3 = useToggle(),
      _useToggle4 = _slicedToArray(_useToggle3, 2),
      isOpen = _useToggle4[0],
      _useToggle4$ = _useToggle4[1],
      openDrawer = _useToggle4$.on,
      closeDrawer = _useToggle4$.off;

  var challengeHeaderRef = useRef(null);
  if (!challengePlayerState.activity && !challengePlayerState.stage && challenge.availableForCompletion) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ChallengeHeader, _objectSpread(_objectSpread({}, challengePlayerState), {}, {
      challengeHeaderRef: challengeHeaderRef,
      challenge: challenge,
      profile: profile,
      onClose: closeChallengePlayer,
      isOnboarding: !!isOnboarding,
      canClose: !!canClose,
      "data-element": "ChallengeHeader",
      "data-component": "PurePlayerBody",
      "data-source-file": "player-body.tsx"
    })), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: 'flex',
        width: 1
      },
      className: isOnboarding ? 'OnboardingChallenge' : 'Challenge',
      "data-element": "Box",
      "data-source-file": "player-body.tsx",
      children: [/*#__PURE__*/_jsxs(FlexibleChallengeContainer, {
        offsetHeight: (_challengeHeaderRef$c = (_challengeHeaderRef$c2 = challengeHeaderRef.current) === null || _challengeHeaderRef$c2 === void 0 ? void 0 : _challengeHeaderRef$c2.offsetHeight) !== null && _challengeHeaderRef$c !== void 0 ? _challengeHeaderRef$c : 0,
        playerBodyRef: playerBodyRef,
        isOpen: isOpen,
        "data-element": "FlexibleChallengeContainer",
        "data-source-file": "player-body.tsx",
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: 1,
            px: 3
          },
          "data-element": "Box",
          "data-source-file": "player-body.tsx",
          children: [challengePlayerState.stage && /*#__PURE__*/_jsx(StageInfo, {
            stage: challengePlayerState.stage,
            totalStages: challenge.totalStages
          }), challenge.multiComplete && challenge.activities && !isOpen && /*#__PURE__*/_jsx(SubmissionsButton, {
            onClick: openDrawer
          })]
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            py: {
              xs: 3,
              md: 5
            },
            mx: 'auto',
            px: 2.5,
            maxWidth: 860,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            width: 1
          },
          "data-element": "Box",
          "data-source-file": "player-body.tsx",
          children: [pipe(getLastTimelineEntry(challengePlayerState.activity), O.chain(O.fromPredicate(NonRejectedCorporateAlertEntries.is)), O.matchW(constNull, function (_ref9) {
            var _challengePlayerState3;

            var __typename = _ref9.__typename,
                admin = _ref9.admin,
                feedback = _ref9.feedback;
            return /*#__PURE__*/_jsx(CorporateConfirmationAlert, {
              status: __typename,
              stage: (_challengePlayerState3 = challengePlayerState.stage) === null || _challengePlayerState3 === void 0 ? void 0 : _challengePlayerState3.position,
              admin: admin,
              feedback: feedback
            });
          })), ((_challengePlayerState4 = challengePlayerState.activity) === null || _challengePlayerState4 === void 0 ? void 0 : _challengePlayerState4.state) === 'closed' || !challenge.availableForCompletion ? /*#__PURE__*/_jsx(CompletionScreen, {
            accruedPoints: (_challengePlayerState5 = (_challengePlayerState6 = challengePlayerState.activity) === null || _challengePlayerState6 === void 0 ? void 0 : _challengePlayerState6.pointsEarned) !== null && _challengePlayerState5 !== void 0 ? _challengePlayerState5 : 0,
            profile: profile,
            currentChallengeId: challenge.id,
            isOnboarding: isOnboarding,
            completeStageStatus: completeStageStatus,
            showAnimation: showCompleteAnimation,
            handleDisableAnimation: disableCompleteAnimation,
            stageCompletions: O.fromNullable(((_challengePlayerState7 = challengePlayerState.activity) === null || _challengePlayerState7 === void 0 ? void 0 : _challengePlayerState7.stageCompletions) || ((_challenge$activities = challenge.activities) === null || _challenge$activities === void 0 ? void 0 : (_challenge$activities2 = _challenge$activities.at(0)) === null || _challenge$activities2 === void 0 ? void 0 : _challenge$activities2.stageCompletions))
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [((_challengePlayerState8 = challengePlayerState.stage) === null || _challengePlayerState8 === void 0 ? void 0 : _challengePlayerState8.id) === challenge.firstStage.id && /*#__PURE__*/_jsx(FirstStageInfo, {
              challenge: challenge
            }), /*#__PURE__*/_jsx(CurrentStage, {
              stage: challengePlayerState.stage || challenge.firstStage,
              activity: challengePlayerState.activity,
              completeStage: completeStage,
              completeStageStatus: completeStageStatus,
              profile: profile,
              isOnboarding: isOnboarding,
              prospect: challenge.prospect
            })]
          })]
        }), pipe(challengePlayerState.stage, O.fromNullable, O.chain(O.fromPredicate(function (s) {
          return PublicResponseStageNames.is(s.__typename);
        })), O.chain(O.fromPredicate(function (s) {
          return !!s.responsesArePublic;
        })), O.match(constNull, function (stage) {
          return /*#__PURE__*/_jsx(PublicResponses, {
            containerSx: {
              py: 5
            },
            profile: profile,
            stage: stage
          });
        }))]
      }), challenge.activities && /*#__PURE__*/_jsx(ActivitySelector, {
        activities: challenge.activities,
        challenge: challenge,
        isOpen: isOpen,
        closeDrawer: closeDrawer,
        container: playerBodyRef.current
      })]
    })]
  });
};
export default Object.assign(flow(useProps, PurePlayerBody), {
  displayName: 'PlayerBody'
});